/**
 * React Query hooks for Knowledge Library CRUD.
 * Replaces Zustand mock data for the Knowledge Library page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createKnowledgeEntry,
  deleteKnowledgeEntry,
  fetchKnowledgeEntries,
  updateKnowledgeEntry,
  type KnowledgeEntryCreate,
} from "@/lib/templateApi";
import { useAuthState } from "./useAuthState";

const QUERY_KEY = "knowledgeEntries";

// ── Queries ───────────────────────────────────────────────────────────────────

export function useKnowledgeEntries(params?: {
  sport?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => fetchKnowledgeEntries(params),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateKnowledgeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: KnowledgeEntryCreate) => createKnowledgeEntry(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateKnowledgeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<KnowledgeEntryCreate> }) =>
      updateKnowledgeEntry(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteKnowledgeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteKnowledgeEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
